/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IActionContext } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    CartLine,
    ChannelDeliveryOptionConfiguration,
    DeliveryOption,
    OrgUnitLocation,
    ProductDeliveryOptions,
    ProductsDataActions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { IStoreSelectorStateManager } from '@msdyn365-commerce-modules/bopis-utilities';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export interface IPickUpInStoreViewProps {
    callbacks: {
        toggleBopis(isBopisSelected: boolean): void;
    };
    defaultComponent?: React.ReactNode;
    orgUnitName?: string;
    isBopisSelected: boolean;
    ContainerProps: INodeProps;
    deliveryOption?: string;
}

export interface IPickUpInStoreProps {
    context: IActionContext;
    cartline: CartLine;
    product: SimpleProduct;
    shipitText: string;
    pickUpInStoreText: string;
    changeStoreText: string;
    cartState: ICartState | undefined;
    storeSelectorStateManager: IStoreSelectorStateManager | undefined;
    orgUnitLocations?: OrgUnitLocation[] | undefined;
    deliveryOptions?: ProductDeliveryOptions;
    pickupDeliveryModeCode?: string;
    storeSelectorModuleId?: string;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    retailMulitplePickupFeatureState?: boolean;
    locationChangedHandler(): void;
}

export const getLineIdFromExtProps = (cartLine: CartLine): [string, boolean] => {
    let lineId = cartLine.ExtensionProperties?.find(ep => ep.Key === 'line-id')?.Value?.StringValue;
    if (lineId) {
        return [lineId, false];
    }

    if (!cartLine.ExtensionProperties) {
        cartLine.ExtensionProperties = [];
    }

    // save the line id in ext properies incase the line id changed due to login
    lineId = cartLine.LineId ?? '';
    cartLine.ExtensionProperties.push({
        Key: 'line-id',
        Value: {
            StringValue: lineId
        }
    });

    return [lineId, true];
};

export const getWarrantyLine = (productLineNumber: number, cartState?: ICartState) => {
    const cartLines = cartState?.cart.CartLines ?? [];
    const linkedCartLine = cartLines.find(cl => {
        return cl.WarrantableLineNumber === productLineNumber;
    });
    return linkedCartLine ?? null;
};

export const getRelatedCartLines = (productCartLine: CartLine, cartState?: ICartState) => {
    const cartLinesToRemove: CartLine[] = [];
    const linkedCartLine = getWarrantyLine(productCartLine.LineNumber!, cartState);
    if (linkedCartLine) {
        cartLinesToRemove.push(linkedCartLine);
    }

    const [productLineId] = getLineIdFromExtProps(productCartLine);

    const cartLines = cartState?.cart.CartLines ?? [];
    for (const cartLine of cartLines) {
        if (
            cartLine.TotalAmount === 0 &&
            cartLine.ExtensionProperties?.find(ex => ex.Key === 'bonus-for-line-id' && ex.Value?.StringValue === productLineId)
        ) {
            cartLinesToRemove.push(cartLine);
        }
    }

    return cartLinesToRemove;
};

export const PickUpInStore = (props: IPickUpInStoreProps): IPickUpInStoreViewProps | undefined => {
    const _onLocationChangedCallback = async (
        orgUnitLocation: OrgUnitLocation,
        cartState: ICartState | undefined,
        cartline: CartLine,
        deliveryMode?: string
    ) => {
        if (!cartState) {
            return Promise.resolve();
        }

        const result = await cartState.updateCartLinePickupLocation({
            cartLineId: cartline.LineId!,
            location: orgUnitLocation,
            deliveryMode
        });

        process.env.NODE_ENV === 'development' && console.log('updateCartLinePickupLocation', result);
        if (result.status === 'SUCCESS') {
            const relatedLines = getRelatedCartLines(cartline, cartState);
            const bonusLines = relatedLines.filter(l => !l.IsWarrantyLine);

            const inStockAtStore: { [id: number]: boolean } = {};

            if (bonusLines.length > 0) {
                const warehouseAvailability = await ProductsDataActions.getEstimatedProductWarehouseAvailabilityAsync(
                    {
                        callerContext: props.context
                    },
                    bonusLines.map(p => ({
                        ProductId: p.ProductId!,
                        InventLocationId: orgUnitLocation.InventoryLocationId,
                        DataAreaId: props.context.requestContext.channel?.InventLocationDataAreaId ?? 'hei'
                    })),
                    null
                );

                for (const availablity of warehouseAvailability.AggregatedProductInventoryAvailabilities ?? []) {
                    inStockAtStore[availablity.ProductId!] = availablity.PhysicalAvailableInventoryLevelCode === 'INSTOCK';
                }

                process.env.NODE_ENV === 'development' &&
                    console.log('updateCartLinePickupLocation', relatedLines, bonusLines, warehouseAvailability);
            }

            for (const relatedLine of relatedLines) {
                if (relatedLine.IsWarrantyLine || inStockAtStore[relatedLine.ProductId!]) {
                    await cartState.updateCartLinePickupLocation({
                        cartLineId: relatedLine.LineId!,
                        location: orgUnitLocation,
                        deliveryMode
                    });
                } else {
                    await cartState.clearCartLinePickupLocation({
                        cartLineId: relatedLine.LineId!
                    });
                }
            }
        }

        if (result.status === 'SUCCESS') {
            props.locationChangedHandler();
        }

        return Promise.resolve();
    };

    const _toggleBOPIS = async (isBopisSelected: boolean) => {
        const { storeSelectorStateManager, product, cartline, cartState, storeSelectorModuleId } = props;

        if (isBopisSelected) {
            if (!storeSelectorStateManager) {
                return;
            }
            storeSelectorStateManager
                .openDialog({
                    id: storeSelectorModuleId,
                    product,
                    alreadySelectedLocation: {
                        OrgUnitNumber: cartline.FulfillmentStoreId
                    },
                    deliveryOptions: props.deliveryOptions,
                    onLocationSelected: async (orgUnitLocation, deliveryMode: string | undefined) => {
                        return _onLocationChangedCallback(orgUnitLocation, cartState, cartline, deliveryMode);
                    }
                })
                .catch(error => {});
        } else {
            if (!cartState) {
                return;
            }
            cartline.DeliveryMode = '';
            cartline.FulfillmentStoreId = '';
            cartline.ShippingAddress = {};
            const result = await cartState.clearCartLinePickupLocation({ cartLineId: cartline.LineId! });

            if (result.status === 'SUCCESS') {
                const relatedLines = getRelatedCartLines(cartline, cartState);
                process.env.NODE_ENV === 'development' && console.log('PickUpInStore', relatedLines);
                for (const relatedLine of relatedLines) {
                    relatedLine.DeliveryMode = '';
                    relatedLine.FulfillmentStoreId = '';
                    relatedLine.ShippingAddress = {};
                    await cartState.clearCartLinePickupLocation({
                        cartLineId: relatedLine.LineId!
                    });
                }
            }

            if (result.status === 'SUCCESS') {
                props.locationChangedHandler();
            }
        }
    };

    const _getOrgUnitName = (fulfillmentStoreId: string | undefined, orgUnitLocations: OrgUnitLocation[] | undefined) => {
        if (!orgUnitLocations || !fulfillmentStoreId || orgUnitLocations.length === 0) {
            return '';
        }

        const foundLocation = orgUnitLocations.find(orgUnitLocation => {
            return orgUnitLocation.OrgUnitNumber === fulfillmentStoreId;
        });

        if (foundLocation) {
            return foundLocation.OrgUnitName;
        }
        return fulfillmentStoreId;
    };

    const _renderShippingMethod = (
        cartLine: CartLine,
        shipItText: string,
        pickupInStoretext: string,
        changeStoreText: string,
        orgUnitLocations: OrgUnitLocation[] | undefined
    ): JSX.Element => {
        const orgUnitName = _getOrgUnitName(cartLine.FulfillmentStoreId, orgUnitLocations);
        const bopisSelected = !!cartLine.FulfillmentStoreId;

        const toggleBOPIS = (isBopisSelected: boolean) => {
            return () => {
                void _toggleBOPIS(isBopisSelected);
            };
        };

        return (
            <>
                <label className='msc-cart-line__bopis-shipping'>
                    <input
                        id={`ms-cart-bopis-ship-option-${cartLine.LineId}`}
                        type='radio'
                        name={`shippingType ${cartLine.LineId}`}
                        onChange={toggleBOPIS(false)}
                        value={shipItText}
                        checked={!bopisSelected}
                        aria-checked={!bopisSelected}
                        key={`${cartLine.LineId}-shipit`}
                    />
                    {shipItText}
                </label>
                <label className='msc-cart-line__bopis-store-pickup'>
                    <input
                        id={`ms-cart-bopis-pickup-option-${cartLine.LineId}`}
                        type='radio'
                        name={`shippingType ${cartLine.LineId}`}
                        onChange={toggleBOPIS(true)}
                        value={pickupInStoretext}
                        checked={bopisSelected}
                        aria-checked={bopisSelected}
                        key={`${cartLine.LineId}-pickup`}
                    />
                    {pickupInStoretext}
                </label>
                {cartLine.FulfillmentStoreId ? (
                    <div className='msc-cart-line__bopis__fullfilment'>
                        <span className='msc-cart-line__bopis__fullfilment-store'>{orgUnitName}</span>
                        <button className='msc-cart-line__bopis-changestore btn' onClick={toggleBOPIS(true)}>
                            {changeStoreText}
                        </button>
                    </div>
                ) : null}
            </>
        );
    };

    const _getDeliveryOption = (pickupStore: IPickUpInStoreProps, deliveryOptions: ProductDeliveryOptions) => {
        const delivery = deliveryOptions?.DeliveryOptions?.find(option => option.Code === pickupStore.pickupDeliveryModeCode);
        return delivery?.Description;
    };

    /**
     * Method to check if we have atleast one common delivery code between product and channel.
     * @param productdeliveryOptions - Product DeliveryOption List.
     * @param storePickUpOptionList - Channel DeliveryOption List.
     * @returns Boolean flag.
     */
    const matchDeliveryOptions = (
        productdeliveryOptions: DeliveryOption[] | undefined,
        storePickUpOptionList: string[] | undefined
    ): boolean => {
        const deliveryOption: string[] = [];
        productdeliveryOptions?.map(delivery => {
            const pickup = storePickUpOptionList?.find(deliveryCode => deliveryCode === delivery.Code);
            if (pickup) {
                deliveryOption.push(pickup);
            }
            return deliveryOption;
        });

        return ArrayExtensions.hasElements(deliveryOption);
    };

    // If no delivery options present on the product, or none of the delivery options
    // match the PickupDeliveryModeCode, that means the item cannot be used for BOPIS
    if (!props.deliveryOptions || !props.deliveryOptions.DeliveryOptions) {
        return undefined;
    }

    if (props.retailMulitplePickupFeatureState && props.channelDeliveryOptionConfig) {
        if (!matchDeliveryOptions(props.deliveryOptions.DeliveryOptions, props.channelDeliveryOptionConfig?.PickupDeliveryModeCodes)) {
            return undefined;
        }
    } else if (
        !props.pickupDeliveryModeCode ||
        !props.deliveryOptions.DeliveryOptions.find(option => option.Code === props.pickupDeliveryModeCode)
    ) {
        return undefined;
    }

    return {
        callbacks: {
            toggleBopis: _toggleBOPIS
        },
        defaultComponent: _renderShippingMethod(
            props.cartline,
            props.shipitText,
            props.pickUpInStoreText,
            props.changeStoreText,
            props.orgUnitLocations
        ),
        orgUnitName: _getOrgUnitName(props.cartline.FulfillmentStoreId, props.orgUnitLocations),
        isBopisSelected: !!props.cartline.FulfillmentStoreId,
        deliveryOption: _getDeliveryOption(props, props.deliveryOptions),
        ContainerProps: {
            className: 'msc-cart-line__bopis-container'
        }
    };
};
